import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Autocomplete,
  Button,
  Grid,
  Card as MuiCard,
  Divider as MuiDivider,
  Stack,
  TablePagination,
  TextField,
  Typography
} from "@mui/material";
import { spacing } from "@mui/system";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import styled from "styled-components/macro";
import { debounce } from "../../../utils/debounce";
import params from "../../../utils/params";

import { deletePreAmaliat, getAllPreAmaliatWP } from "../../../api/predefinedAmaliat";
import PredefinedAmaliatTable from "../../../components/table/amaliat/PredefinedAmaliatTable";
import ContentLoader from "../../../components/ui/ContentLoader";
import CustomBreadcrumbs from "../../../components/ui/CustomBreadcrumbs";
import NotFound from "../../../components/ui/NotFound";
import useAuth from "../../../hooks/useAuth";
import { PREDEFINED_AMALIAT } from "../../../routesPath";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const PredefinedAmaliat = () => {
  const { user, previleges } = useAuth();

  // Privleges Constants

  const CAN_CREATE = previleges?.includes("PREDEFINED_AMALIYAT_CREATE");
  const CAN_DELETE = previleges?.includes("PREDEFINED_AMALIYAT_DELETE");
  const CAN_UPDATE = previleges?.includes("PREDEFINED_AMALIYAT_UPDATE");

  // states
  const [errorMessage, setErrorMessage] = useState("");
  const [allPredefinedAmaliat, setAllPredefinedAmaliat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [count, setCount] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [search, setSearch] = useState({
    title: "",
    subTitle: "",
    active: "",
  });
  const [filterBy, setFilterBy] = useState("title");

  // objects
  const navigate = useNavigate();

  const filters = [
    {
      label: "Title",
      value: "title",
    },
    // {
    //   label: "Sub Title",
    //   value: "subTitle",
    // },
    {
      label: "Active",
      value: "active",
    },
  ];

  const statusOption = [
    {
      title: "Active",
      value: "true",
    },
    {
      title: "Inactive",
      value: "false",
    },
  ];

  const breadcrumbsList = [
    {
      name: "Dashboard",
      url: "/",
    },
    {
      name: "Predefined Amaliyat List",
      url: "#",
    },
  ];

  // functions
  // fetch All Predefined Amaliat
  const fetchAllPredefinedAmaliatFn = async () => {
    setLoading(true);

    try {
      let response = await getAllPreAmaliatWP(
        params({
          pageNo: isSearching ? "" : currentPage,
          pageSize: rowsPerPage,
          sortBy: "lastModifiedDate",
          ascOrDesc: "desc",
          title: search.title,
          subTitle: search.subTitle,
          active: search.active,
          loadAmaliyat: true,
        })
      );
      setAllPredefinedAmaliat(response.data.payload.content);
      setCount(response.data.payload.totalElements);
      setRowsPerPage(response.data.payload.pageable.pageSize);
      setCurrentPage(response.data.payload.pageable.pageNumber);
      setLoading(false);
      setIsSearching(false);
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  // searching
  const handleSearch = (e) => {
    setIsSearching(true);
    console.log("handle search clicked");
    let value;
    if (filterBy === "recommended") {
      value = e?.value;
    } else {
      value = e?.target?.value;
    }
    setSearch({ [filterBy]: value });

    if (value?.length > 0) {
      setCurrentPage(0);
      console.log("Set Current Page to 0");
    }
  };

  // filtering
  const handleFilter = (e) => {
    console.log("Handle Filter clicked!");
    const filter = e?.target?.value;
    setFilterBy(filter);
    setSearch({});
  };

  // table pagination
  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(e?.target?.value);
  };

  const hideDeleteModal = () => {
    setErrorMessage("");
    setShowDelete(false);
  };

  const deletePredefinedAmaliat = async (categoryId, amaliyatId) => {
    setIsDeleting(true);
    try {
      await deletePreAmaliat(categoryId, amaliyatId);
      fetchAllPredefinedAmaliatFn();
    } catch (error) {
      console.log("Error deleting predefined amaliyat:", error);
      setErrorMessage(
        error?.response?.data?.message ||
        "Something went wrong, please try again later"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // use-effect
  useEffect(() => {
    fetchAllPredefinedAmaliatFn();
  }, [currentPage, rowsPerPage, search]);

  return (
    <React.Fragment>
      <Helmet title="Predefined Amaliyat - List" />

      <Grid
        justifyContent="space-between"
        alignItems={"end"}
        container
        spacing={10}
      >
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Predefined Amaliyat List
          </Typography>

          <CustomBreadcrumbs breadcrumbsList={breadcrumbsList} />
        </Grid>

        {CAN_CREATE && (
          <Grid item>
            <Button
              onClick={() => navigate(`${PREDEFINED_AMALIAT}/add`)}
              variant="contained"
              color="primary"
              sx={{ ml: 2 }}
            >
              <AddCircleOutlineIcon sx={{ mr: 1 }} />
              Add New Predefined Amaliyat
            </Button>
          </Grid>
        )}
      </Grid>

      <Divider my={3} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card sx={{ p: 5 }}>
            <Stack direction="row" gap={3} mb={3}>
              <TextField
                id="outlined-native"
                select
                size="small"
                label="Filter By"
                value={filterBy}
                onChange={handleFilter}
                SelectProps={{
                  native: true,
                }}
              >
                {filters.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>

              {filterBy === "active" ? (
                <Autocomplete
                  sx={{ width: 200 }}
                  id="tags-outlined"
                  options={statusOption}
                  value={null}
                  getOptionLabel={(option) => option.title}
                  onChange={(event, value) => handleSearch(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="search"
                      label="Select Active Status"
                      my={2}
                      size="small"
                    />
                  )}
                />
              ) : (
                <TextField
                  size="small"
                  label={"Search Here..."}
                  variant="outlined"
                  onChange={(e) => debounce(() => handleSearch(e), 800)}
                />
              )}
            </Stack>
            {loading ? (
              <ContentLoader />
            ) : (
              <>
                {allPredefinedAmaliat.length < 1 ? (
                  <NotFound />
                ) : (
                  <>
                    <PredefinedAmaliatTable
                      allPredefinedAmaliat={allPredefinedAmaliat}
                      can_update={CAN_UPDATE}
                      can_delete={CAN_DELETE}
                      pageNo={currentPage}
                      rowsPerPage={rowsPerPage}
                      deletePredefinedAmaliat={deletePredefinedAmaliat}
                    />

                    <TablePagination
                      rowsPerPageOptions={[20, 50, 100]}
                      count={count}
                      page={currentPage}
                      rowsPerPage={rowsPerPage}
                      onPageChange={(_, page) => {
                        handleChangePage(page);
                      }}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      component="div"
                    />
                  </>
                )}
              </>
            )}
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default PredefinedAmaliat;